import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Users as UsersIcon,
  X as XIcon,
  Plus as PlusIcon,
  DownloadCloud as DownloadCloudIcon,
} from 'lucide-react';
import {
  createEmpresa,
  deleteEmpresa,
  getConfigSugeridaEmpresa,
  listEmpresas,
  listUsuariosDeEmpresa,
  updateEmpresa,
  verificarEmpresaOracle,
  type UpdateEmpresaPayload,
} from '@lib/services/empresas.service';
import { PlanesLista, type PlanForm } from '@components/empresas/PlanesLista';
import { fusionar, resumenVacio, type ResumenOracle } from '@lib/empresas/fusionar-config';
import { planesAPayload } from '@lib/empresas/planes-payload';
import {
  listUsuarios,
  asignarEmpresaAUsuario,
  quitarEmpresaDeUsuario,
} from '@lib/services/usuarios.service';
import { useAuthStore } from '@lib/store/auth.store';
import { ROL_LABELS, UserRole, type Empresa, type UsuarioListItem } from '@lib/types';
import { useCanMutate } from '@lib/hooks/useCanMutate';
import { Spinner, TableSkeleton } from '@components/medicus-ui/Loaders';
import { ErrorInline } from '@components/ErrorInline';
import { DdjjPreguntasSelector } from '@components/empresas/DdjjPreguntasSelector';

interface FormState {
  cuit: string;
  razonSocial: string;
  nombreComercial: string;
  contactoNombre: string;
  contactoEmail: string;
  contactoTelefono: string;
  oracleEmplId: string;
  // Config de alta directa (Binary) — códigos compartidos por la empresa.
  altaHabilitada: boolean;
  // Sin asistente del titular: los legajos pasan directo al alta al validar el padrón.
  sinWizard: boolean;
  obraSocialCodigo: string;
  promotorId: string;
  firmaSolicitud: string;
  agenciaEnvio: string;
  // Planes (base + opciones), cada uno con sus códigos por plan.
  planes: PlanForm[];
  // Preguntas del catálogo de DDJJ que esta empresa NO hace (códigos).
  ddjjExcluidas: string[];
}

function planVacio(esBase = false): PlanForm {
  return {
    codigo: '',
    nombre: '',
    sucursalId: '',
    categoriaAfiliadoCodigo: '',
    aaCodigo: '',
    aa2Codigo: '',
    esBase,
  };
}

const EMPTY: FormState = {
  cuit: '',
  razonSocial: '',
  nombreComercial: '',
  contactoNombre: '',
  contactoEmail: '',
  contactoTelefono: '',
  oracleEmplId: '',
  altaHabilitada: false,
  sinWizard: false,
  obraSocialCodigo: '',
  promotorId: '',
  firmaSolicitud: '',
  agenciaEnvio: '',
  planes: [],
  ddjjExcluidas: [],
};

export function EmpresasPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  // USER_MEDICUS ve el listado pero no crea/elimina organizaciones.
  const puedeMutar = useCanMutate();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [empresaUsuarios, setEmpresaUsuarios] = useState<Empresa | null>(null);
  const [editing, setEditing] = useState<Empresa | null>(null);
  const [oracleNotas, setOracleNotas] = useState<string[]>([]);
  const [oracleResumen, setOracleResumen] = useState<ResumenOracle>(resumenVacio());

  const empresas = useQuery({
    queryKey: ['empresas', search],
    queryFn: () => listEmpresas(search || undefined),
  });

  const emplIdNum = Number.parseInt(form.oracleEmplId, 10);
  const emplIdValido = Number.isFinite(emplIdNum) && emplIdNum > 0;
  const checkEmpresa = useQuery({
    queryKey: ['oracle-empresa-check', emplIdNum],
    queryFn: () => verificarEmpresaOracle(emplIdNum),
    enabled: showForm && emplIdValido,
    retry: false,
    staleTime: 60_000,
  });

  const create = useMutation({
    mutationFn: createEmpresa,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] });
      setShowForm(false);
      setForm(EMPTY);
      setError(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message ?? 'No se pudo crear la organización');
    },
  });

  const updateMut = useMutation({
    mutationFn: (vars: { id: string; payload: UpdateEmpresaPayload }) =>
      updateEmpresa(vars.id, vars.payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] });
      closeForm();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message ?? 'No se pudo guardar la organización');
    },
  });

  const remove = useMutation({
    mutationFn: deleteEmpresa,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] });
      // La sesión guarda las organizaciones accesibles y la activa: si se borró
      // la activa, hay que refrescar /me ya, no esperar al próximo reload.
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const update = (k: keyof FormState, v: string | boolean | string[]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // --- Planes (base + opciones) ---
  const setPlan = (i: number, k: keyof PlanForm, v: string) =>
    setForm((p) => ({
      ...p,
      planes: p.planes.map((pl, idx) => (idx === i ? { ...pl, [k]: v } : pl)),
    }));
  const addPlan = () =>
    setForm((p) => ({ ...p, planes: [...p.planes, planVacio(p.planes.length === 0)] }));
  const removePlan = (i: number) =>
    setForm((p) => {
      const planes = p.planes.filter((_, idx) => idx !== i);
      // Si quité el base y queda alguno, marco el primero como base.
      if (planes.length > 0 && !planes.some((pl) => pl.esBase)) planes[0].esBase = true;
      return { ...p, planes };
    });
  const setBase = (i: number) =>
    setForm((p) => ({ ...p, planes: p.planes.map((pl, idx) => ({ ...pl, esBase: idx === i })) }));

  // Como mucho hay UN plan inicial: marcar uno desmarca al resto, y `-1`
  // desmarca todos (la organización vuelve a dar altas con el plan base).
  // Un plan sin el flag cuenta como disponible, así que solo se guarda cuando
  // se apaga o se vuelve a prender explícitamente.
  const setVenta = (i: number, valor: boolean) =>
    setForm((p) => ({
      ...p,
      planes: p.planes.map((pl, idx) => (idx === i ? { ...pl, disponibleVenta: valor } : pl)),
    }));

  // --- Traer la config de Oracle por empl_id ---
  const traer = useMutation({
    mutationFn: () => getConfigSugeridaEmpresa(emplIdNum),
    onSuccess: (cfg) => {
      // Mismo criterio que en `/empresas/:id`: Oracle completa lo vacío, no pisa
      // lo cargado a mano, y se informa qué completó y en qué difiere. El cálculo
      // va fuera del updater porque `fusionar` acumula mensajes y StrictMode
      // ejecuta los updaters dos veces.
      const resumen = resumenVacio();
      const razonSocial = fusionar('Razón social', form.razonSocial, cfg.identidad.nombre, resumen);
      const cuit = fusionar('CUIT', form.cuit, cfg.identidad.cuit, resumen);
      const nombreComercial = fusionar(
        'Nombre comercial',
        form.nombreComercial,
        cfg.identidad.nombre,
        resumen,
        false,
      );
      setForm((p) => ({
        ...p,
        razonSocial,
        cuit,
        nombreComercial,
        obraSocialCodigo: cfg.obraSocialCodigo ?? p.obraSocialCodigo,
        promotorId: cfg.promotorId ?? p.promotorId,
        firmaSolicitud: cfg.firmaSolicitud ?? p.firmaSolicitud,
        agenciaEnvio: cfg.agenciaEnvio || p.agenciaEnvio || '1',
        planes: cfg.planes.length
          ? cfg.planes.map((pl, i) => ({
              codigo: pl.codigo,
              nombre: pl.nombre,
              sucursalId: pl.sucursalId,
              categoriaAfiliadoCodigo: pl.categoriaAfiliadoCodigo,
              aaCodigo: pl.aaCodigo,
              aa2Codigo: pl.aa2Codigo,
              esBase: i === 0,
            }))
          : p.planes,
      }));
      if (cfg.planes.length) resumen.aplicados.push(`${cfg.planes.length} plan(es) de Oracle`);
      resumen.procedencia.push(...(cfg.procedencia ?? []));
      setOracleResumen(resumen);
      setOracleNotas(cfg.notas ?? []);
      setError(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message ?? 'No se pudo traer la config de Oracle');
    },
  });

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY);
    setError(null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError(null);
    setShowForm(true);
  };

  const guardando = create.isPending || updateMut.isPending;

  const submitForm = () => {
    const planes = planesAPayload(form.planes);
    // Mantengo planBaseCodigo/planSuperadorCodigo a nivel empresa (back-compat con
    // el cálculo de MS2): base = plan base; superador = primer plan no-base con
    // OTRO código (el mismo plan en otra sucursal es la misma cobertura).
    const base = planes.find((p) => p.esBase) ?? planes[0];
    const superador = planes.find((p) => !p.esBase && p.codigo !== base?.codigo);
    const payload = {
      cuit: form.cuit,
      razonSocial: form.razonSocial,
      nombreComercial: form.nombreComercial,
      contactoNombre: form.contactoNombre,
      contactoEmail: form.contactoEmail,
      contactoTelefono: form.contactoTelefono || undefined,
      oracleEmplId: emplIdValido ? emplIdNum : undefined,
      planBaseCodigo: base?.codigo,
      planSuperadorCodigo: superador?.codigo,
      altaConfig: {
        habilitada: form.altaHabilitada,
        obraSocialCodigo: form.obraSocialCodigo || undefined,
        promotorId: form.promotorId || undefined,
        firmaSolicitud: form.firmaSolicitud || undefined,
        agenciaEnvio: form.agenciaEnvio || undefined,
        planes,
      },
      ddjjConfig: { preguntasExcluidas: form.ddjjExcluidas },
      sinWizard: form.sinWizard,
    };
    if (editing) updateMut.mutate({ id: editing.id, payload });
    else create.mutate(payload);
  };

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Organizaciones</h1>
        <p className="text-element-mid">Gestión de organizaciones que pueden cargar padrones.</p>
      </header>

      <div className="rowBetween" style={{ marginTop: '1rem' }}>
        <input
          className="input"
          style={{ maxWidth: 360 }}
          placeholder="Buscar por razón social, nombre o CUIT…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {puedeMutar && (
          <button className="btn btn-primary" onClick={openCreate}>
            <span className="btn-font-style">Nueva organización</span>
          </button>
        )}
      </div>

      {showForm && (
        <div
          className="modalBackdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            if (!guardando) closeForm();
          }}
        >
          <form
            className="modalCard"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <header className="modalHeader">
              <h2 className="heading-5" style={{ margin: 0 }}>
                {editing ? 'Editar organización' : 'Nueva organización'}
              </h2>
              <button
                type="button"
                className="modalClose"
                onClick={closeForm}
                aria-label="Cerrar"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <XIcon size={18} />
              </button>
            </header>

            <div className="modalGrid">
              <label className="formField">
                <span>CUIT (11 dígitos sin guiones)</span>
                <input
                  className="input"
                  value={form.cuit}
                  onChange={(e) => update('cuit', e.target.value)}
                  required
                />
              </label>
              <label className="formField">
                <span>Razón social</span>
                <input
                  className="input"
                  value={form.razonSocial}
                  onChange={(e) => update('razonSocial', e.target.value)}
                  required
                />
              </label>
              <label className="formField">
                <span>Nombre comercial</span>
                <input
                  className="input"
                  value={form.nombreComercial}
                  onChange={(e) => update('nombreComercial', e.target.value)}
                  required
                />
              </label>
              <label className="formField">
                <span>Contacto — Nombre</span>
                <input
                  className="input"
                  value={form.contactoNombre}
                  onChange={(e) => update('contactoNombre', e.target.value)}
                  required
                />
              </label>
              <label className="formField">
                <span>Contacto — Email</span>
                <input
                  className="input"
                  type="email"
                  value={form.contactoEmail}
                  onChange={(e) => update('contactoEmail', e.target.value)}
                  required
                />
              </label>
              <label className="formField">
                <span>Contacto — Teléfono (opcional)</span>
                <input
                  className="input"
                  value={form.contactoTelefono}
                  onChange={(e) => update('contactoTelefono', e.target.value)}
                />
              </label>
              <label className="formField">
                <span>
                  EMPL_ID Oracle (opcional)
                  <small
                    style={{
                      display: 'block',
                      fontSize: 11,
                      color: 'var(--color-element-mid)',
                      marginTop: 2,
                    }}
                  >
                    ID del empleador en SALUD.SA_EMPLEADORES. Lo usa Binary para el alta.
                  </small>
                </span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={form.oracleEmplId}
                  onChange={(e) => update('oracleEmplId', e.target.value)}
                  placeholder="ej: 4091"
                />
                {emplIdValido && (
                  <EmpresaCheckBadge
                    isLoading={checkEmpresa.isLoading}
                    data={checkEmpresa.data}
                    error={checkEmpresa.error}
                  />
                )}
              </label>
              <div
                style={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={!emplIdValido || traer.isPending}
                  onClick={() => traer.mutate()}
                  title={!emplIdValido ? 'Cargá un empl_id válido primero' : ''}
                >
                  <DownloadCloudIcon size={14} />{' '}
                  {traer.isPending ? 'Trayendo…' : 'Traer config de Oracle'}
                </button>
                <small className="text-element-mid" style={{ fontSize: 11 }}>
                  Autocompleta identidad, planes y códigos desde el empl_id.
                </small>
              </div>
              {(oracleResumen.aplicados.length > 0 ||
                oracleResumen.distintos.length > 0 ||
                oracleResumen.procedencia.length > 0) && (
                <div className="card" style={{ gridColumn: '1 / -1', padding: 10 }}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {oracleResumen.aplicados.map((n) => (
                      <li key={n} style={{ fontSize: 12, color: '#166534' }}>
                        Se completó — {n}
                      </li>
                    ))}
                    {oracleResumen.procedencia.map((n) => (
                      <li key={n} className="text-element-mid" style={{ fontSize: 12 }}>
                        {n}
                      </li>
                    ))}
                    {oracleResumen.distintos.map((n) => (
                      <li key={n} style={{ fontSize: 12, color: '#b45309' }}>
                        {n}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {oracleNotas.length > 0 && (
                <div
                  className="card"
                  style={{ gridColumn: '1 / -1', borderColor: 'var(--color-warning)', padding: 10 }}
                >
                  <strong className="status status-warning" style={{ fontSize: 12 }}>
                    Lo que no se pudo derivar (completá a mano):
                  </strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {oracleNotas.map((n) => (
                      <li key={n} style={{ fontSize: 12 }}>
                        {n}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                style={{
                  gridColumn: '1 / -1',
                  borderTop: '1px solid var(--color-outline)',
                  paddingTop: 12,
                  marginTop: 4,
                }}
              >
                <h3 className="heading-6" style={{ margin: 0 }}>
                  Alta directa (Binary)
                </h3>
                <p className="text-element-mid" style={{ fontSize: 11, margin: '2px 0 0' }}>
                  La obra social, el promotor y la firma son de la empresa. La sucursal, la
                  categoría y los AA van POR PLAN (abajo). MS (74000): obra social 112, promotor
                  PROMDIR, firma SALUD; planes MS_12490 (suc 74000, base) y MS_12495R (74001).
                </p>
              </div>
              <label
                className="formField"
                style={{ gridColumn: '1 / -1', flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={form.altaHabilitada}
                  onChange={(e) => update('altaHabilitada', e.target.checked)}
                />
                <span style={{ margin: 0 }}>Habilitar alta por API para esta organización</span>
              </label>
              <label
                className="formField"
                style={{ gridColumn: '1 / -1', flexDirection: 'row', alignItems: 'center', gap: 8 }}
                title="Al validar un padrón, los legajos no reciben la invitación al onboarding (OTP, datos, DDJJ, plan): pasan directo a «Listo para alta» y el despacho a Binary los toma. Los bis del padrón quedan activados."
              >
                <input
                  type="checkbox"
                  checked={form.sinWizard}
                  onChange={(e) => update('sinWizard', e.target.checked)}
                />
                <span style={{ margin: 0 }}>
                  Sin asistente del titular: los legajos pasan directo al alta al validar el padrón
                </span>
              </label>
              <label className="formField">
                <span>Obra social (OSOC_CODIGO)</span>
                <input
                  className="input"
                  value={form.obraSocialCodigo}
                  onChange={(e) => update('obraSocialCodigo', e.target.value)}
                  placeholder="ej: 112"
                />
              </label>
              <label className="formField">
                <span>Promotor (Binary)</span>
                <input
                  className="input"
                  value={form.promotorId}
                  onChange={(e) => update('promotorId', e.target.value)}
                  placeholder="ej: PROMDIR"
                />
              </label>
              <label className="formField">
                <span>Firma (usuario)</span>
                <input
                  className="input"
                  value={form.firmaSolicitud}
                  onChange={(e) => update('firmaSolicitud', e.target.value)}
                  placeholder="ej: SALUD"
                />
              </label>
              <label className="formField">
                <span>Agencia de envío</span>
                <input
                  className="input"
                  value={form.agenciaEnvio}
                  onChange={(e) => update('agenciaEnvio', e.target.value)}
                  placeholder="ej: 1"
                />
              </label>
              <PlanesEditor
                planes={form.planes}
                onSetPlan={setPlan}
                onAddPlan={addPlan}
                onRemovePlan={removePlan}
                onSetBase={setBase}
                onToggleVenta={setVenta}
              />
            </div>

            <div className="formSection" style={{ marginTop: '1.25rem' }}>
              <h3 className="heading-6" style={{ margin: '0 0 4px' }}>
                Declaración jurada de salud
              </h3>
              <p className="text-element-mid" style={{ margin: '0 0 10px', fontSize: 12 }}>
                Preguntas que se le hacen al titular y a su grupo familiar en el onboarding.
              </p>
              <DdjjPreguntasSelector
                value={form.ddjjExcluidas}
                onChange={(excluidas) => update('ddjjExcluidas', excluidas)}
                disabled={guardando}
              />
            </div>

            {error && (
              <p className="status status-error" style={{ marginTop: '1rem' }}>
                {error}
              </p>
            )}

            <div className="modalFooter">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={closeForm}
                disabled={guardando}
              >
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={guardando}>
                <span className="btn-font-style">
                  {guardando ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear organización'}
                </span>
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ marginTop: '1rem' }}>
        {/* Eliminar fallaba en silencio (409 por padrones asociados, 403). */}
        <ErrorInline
          error={remove.error}
          fallback="No se pudo eliminar la organización."
          style={{ marginBottom: 8 }}
        />
        {empresas.isLoading && <TableSkeleton rows={6} />}
        {empresas.data && empresas.data.length === 0 && (
          <p className="text-element-mid">Aún no hay organizaciones cargadas.</p>
        )}
        {empresas.data && empresas.data.length > 0 && (
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Razón social</th>
                  <th>Nombre comercial</th>
                  <th>CUIT</th>
                  <th>Contacto</th>
                  <th>Estado</th>
                  {puedeMutar && <th></th>}
                </tr>
              </thead>
              <tbody>
                {empresas.data.map((e) => (
                  <tr key={e.id}>
                    <td>{e.razonSocial}</td>
                    <td>{e.nombreComercial}</td>
                    <td>{e.cuit}</td>
                    <td>
                      <div>{e.contactoNombre}</div>
                      <div className="text-element-mid" style={{ fontSize: 12 }}>
                        {e.contactoEmail}
                      </div>
                    </td>
                    <td>
                      <span className={e.activa ? 'status status-success' : 'status status-error'}>
                        {e.activa ? 'activa' : 'inactiva'}
                      </span>
                      {e.altaConfig?.habilitada && (
                        <span className="status status-success" style={{ marginLeft: 6 }}>
                          altas API
                        </span>
                      )}
                      {e.sinWizard && (
                        <span
                          className="status status-info"
                          style={{ marginLeft: 6 }}
                          title="Los padrones validados pasan directo al alta, sin asistente del titular"
                        >
                          sin asistente
                        </span>
                      )}
                    </td>
                    {puedeMutar && (
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          className="btn btn-link"
                          onClick={() => navigate(`/empresas/${e.id}`)}
                          style={{ marginRight: 8 }}
                        >
                          Abrir
                        </button>
                        {user?.rol === UserRole.SUPERADMIN_MEDICUS && (
                          <>
                            <button
                              className="btn btn-link"
                              onClick={() => navigate(`/empresas/${e.id}?tab=usuarios`)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                marginRight: 8,
                              }}
                            >
                              <UsersIcon size={13} /> Ver usuarios
                            </button>
                            <button
                              className="btn btn-link"
                              disabled={remove.isPending && remove.variables === e.id}
                              onClick={() => {
                                if (confirm(`¿Eliminar organización "${e.razonSocial}"?`)) {
                                  remove.mutate(e.id);
                                }
                              }}
                              style={{ color: 'var(--color-error-dark)' }}
                            >
                              {remove.isPending && remove.variables === e.id ? (
                                <>
                                  <Spinner size={12} /> Eliminando…
                                </>
                              ) : (
                                'Eliminar'
                              )}
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {empresaUsuarios && (
        <UsuariosEmpresaModal empresa={empresaUsuarios} onClose={() => setEmpresaUsuarios(null)} />
      )}
    </section>
  );
}

function UsuariosEmpresaModal({ empresa, onClose }: { empresa: Empresa; onClose: () => void }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState('');
  const [error, setError] = useState<string | null>(null);

  const usuariosDeLaEmpresa = useQuery({
    queryKey: ['empresa-usuarios', empresa.id],
    queryFn: () => listUsuariosDeEmpresa(empresa.id),
  });

  // Lista de TODOS los usuarios USUARIO_EMPRESA (para ofrecer agregar
  // alguno que aún no esté asignado a esta empresa).
  const todosUsuarios = useQuery({
    queryKey: ['usuarios-emp-todos'],
    queryFn: () => listUsuarios({ rol: UserRole.USUARIO_EMPRESA }),
    enabled: adding,
  });

  const asignar = useMutation({
    mutationFn: (usuarioId: string) => asignarEmpresaAUsuario(usuarioId, empresa.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresa-usuarios', empresa.id] });
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      setPick('');
      setAdding(false);
      setError(null);
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(err.response?.data?.error?.message ?? 'No se pudo asignar el usuario.');
    },
  });

  const quitar = useMutation({
    mutationFn: (usuarioId: string) => quitarEmpresaDeUsuario(usuarioId, empresa.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresa-usuarios', empresa.id] });
      qc.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });

  const idsActuales = new Set((usuariosDeLaEmpresa.data ?? []).map((u) => u.id));
  const noAsignados = (todosUsuarios.data ?? []).filter((u) => !idsActuales.has(u.id));

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 14,
          width: 560,
          maxWidth: '92vw',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 12,
          }}
        >
          <div>
            <h2 className="heading-5">Usuarios de {empresa.razonSocial}</h2>
            <p className="text-element-mid" style={{ fontSize: 12, marginTop: 4 }}>
              CUIT {empresa.cuit} · {usuariosDeLaEmpresa.data?.length ?? 0} usuario(s) asignado(s)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
            }}
            aria-label="Cerrar"
          >
            <XIcon size={18} />
          </button>
        </header>

        {usuariosDeLaEmpresa.isLoading && <p className="text-element-mid">Cargando usuarios…</p>}
        {usuariosDeLaEmpresa.data && usuariosDeLaEmpresa.data.length === 0 && (
          <p className="text-element-mid" style={{ marginTop: 12 }}>
            Esta organización todavía no tiene usuarios asignados.
          </p>
        )}
        {usuariosDeLaEmpresa.data && usuariosDeLaEmpresa.data.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0' }}>
            {usuariosDeLaEmpresa.data.map((u: UsuarioListItem) => (
              <li
                key={u.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--color-outline)',
                  marginBottom: 6,
                }}
              >
                <div>
                  <strong style={{ fontSize: 13 }}>{u.nombre}</strong>
                  <p className="text-element-mid" style={{ fontSize: 11, margin: '2px 0 0' }}>
                    {u.email} · {ROL_LABELS[u.rol]}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-link"
                  disabled={quitar.isPending}
                  onClick={() => quitar.mutate(u.id)}
                  style={{ fontSize: 12, color: 'var(--color-error-dark)' }}
                >
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <p className="status status-error" style={{ display: 'block', marginTop: 8 }}>
            {error}
          </p>
        )}

        {!adding ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setAdding(true)}
            style={{
              marginTop: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <PlusIcon size={14} /> Asignar usuario existente
          </button>
        ) : (
          <div
            style={{
              marginTop: 8,
              padding: 12,
              border: '1px solid var(--color-outline)',
              borderRadius: 10,
              background: '#F9FAFB',
            }}
          >
            <p style={{ fontSize: 13, marginBottom: 8 }}>
              Elegí un usuario para asignarlo a <strong>{empresa.razonSocial}</strong>. El usuario
              hereda acceso de inmediato, sin confirmación de su parte.
            </p>
            {todosUsuarios.isLoading && (
              <p className="text-element-mid">Cargando lista de usuarios…</p>
            )}
            {todosUsuarios.data && noAsignados.length === 0 && (
              <p className="text-element-mid">
                No hay usuarios disponibles para asignar. Creá uno desde la sección "Usuarios"
                primero.
              </p>
            )}
            {noAsignados.length > 0 && (
              <select
                className="input"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">— elegir usuario —</option>
                {noAsignados.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre} ({u.email})
                  </option>
                ))}
              </select>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!pick || asignar.isPending}
                onClick={() => pick && asignar.mutate(pick)}
              >
                <span className="btn-font-style">
                  {asignar.isPending ? 'Asignando…' : 'Asignar'}
                </span>
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setAdding(false);
                  setPick('');
                  setError(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const BADGE_STYLE_BASE = { marginTop: 6, fontSize: 12, display: 'block' as const };
const BADGE_COLOR_NEUTRAL = 'var(--color-element-mid)';
const BADGE_COLOR_ERROR = '#B91C1C';
const BADGE_COLOR_OK = '#16A34A';

function isOracleDeshabilitado(error: unknown): boolean {
  const e = error as { response?: { data?: { error?: { code?: string } } } };
  return e?.response?.data?.error?.code === 'ORACLE_DESHABILITADO';
}

function plural(n: number, singular: string, pluralForm: string): string {
  return n === 1 ? singular : pluralForm;
}

function PlanesEditor({
  planes,
  onSetPlan,
  onAddPlan,
  onRemovePlan,
  onSetBase,
  onToggleVenta,
}: {
  planes: PlanForm[];
  onSetPlan: (i: number, k: keyof PlanForm, v: string) => void;
  onAddPlan: () => void;
  onRemovePlan: (i: number) => void;
  onSetBase: (i: number) => void;
  onToggleVenta: (i: number, valor: boolean) => void;
}) {
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <PlanesLista
        planes={planes}
        onSetPlan={onSetPlan}
        onAddPlan={onAddPlan}
        onRemovePlan={onRemovePlan}
        onSetBase={onSetBase}
        onToggleVenta={onToggleVenta}
      />
    </div>
  );
}

function EmpresaCheckBadge({
  isLoading,
  data,
  error,
}: {
  isLoading: boolean;
  data?: { existe: boolean; planesVigentes: number; afiliadosActivos: number };
  error: unknown;
}) {
  if (isLoading) {
    return (
      <small style={{ ...BADGE_STYLE_BASE, color: BADGE_COLOR_NEUTRAL }}>
        Verificando en Oracle…
      </small>
    );
  }
  if (isOracleDeshabilitado(error)) {
    return (
      <small style={{ ...BADGE_STYLE_BASE, color: BADGE_COLOR_NEUTRAL }}>
        No se pudo verificar (Oracle deshabilitado en este ambiente).
      </small>
    );
  }
  if (!data) return null;
  if (!data.existe) {
    return (
      <small style={{ ...BADGE_STYLE_BASE, color: BADGE_COLOR_ERROR }}>
        ✗ EMPL_ID no encontrado en Oracle (0 planes vigentes y 0 afiliados activos).
      </small>
    );
  }
  const planesLabel = plural(data.planesVigentes, 'plan vigente', 'planes vigentes');
  const afiliados = data.afiliadosActivos.toLocaleString('es-AR');
  const afiliadosLabel = plural(data.afiliadosActivos, 'afiliado activo', 'afiliados activos');
  return (
    <small style={{ ...BADGE_STYLE_BASE, color: BADGE_COLOR_OK }}>
      ✓ Organización encontrada · {data.planesVigentes} {planesLabel} · {afiliados} {afiliadosLabel}
      .
    </small>
  );
}
